import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Rocket } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    if (e) e.preventDefault();
    setBusy(true);
    try {
      const u = await login(email.trim().toLowerCase(), pw);
      const role = u?.role;
      const nucleoNome = u?.nucleo_nome;
      const home = role === "fiscal" ? "/fiscal"
        : role === "analista_mkt" ? "/novo-lead"
        : role === "indicador" ? "/minhas-indicacoes"
        : role === "gestao_cel" ? "/cel/kanban"
        : (role === "vendedora" && nucleoNome === "CeL Consultoria") ? "/cel/kanban"
        : "/funil";
      nav(home);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "E-mail ou senha inválidos");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2">
      {/* Left brand — group photo with smoky black overlay */}
      <div className="hidden md:flex relative flex-col justify-between p-12 overflow-hidden text-white">
        {/* Background image */}
        <div className="absolute inset-0 overflow-hidden">
          <img
            src="https://customer-assets-wrfwihn1.emergentagent.net/job_dashboard-ceap/artifacts/s1ynd08y_MAU03369%20%281%29.webp"
            alt=""
            className="w-full h-full object-cover"
            style={{ objectPosition: "center center" }}
          />
        </div>
        {/* Smoky black gradient overlays */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/60 to-black/20" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-black/70" />
        {/* Subtle grain */}
        <div className="absolute inset-0 opacity-[0.08] mix-blend-overlay"
             style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")" }} />

        {/* Content */}
        <div className="relative z-10">
          <img
            src="https://customer-assets-wrfwihn1.emergentagent.net/job_dashboard-ceap/artifacts/b4qjckp1_nova_logo_CEAP_2020-removebg-preview.png"
            alt="CEAP Brasil"
            className="h-14 md:h-16 w-auto"
            style={{ filter: "brightness(0) invert(1)" }}
          />
        </div>

        <div className="relative z-10 max-w-xl">
          <h1 className="font-display text-3xl lg:text-4xl xl:text-5xl font-semibold leading-[1.15] tracking-tight text-white">
            O <span className="text-blue-400 font-bold">RESULTADO</span> do mês é<br />
            só o espelho da sua <span className="text-blue-400 font-bold">ROTINA</span>.
          </h1>
          <p className="mt-6 text-base md:text-lg text-white/80 font-light">
            Você não <span className="text-white font-semibold">VENDE</span> cursos.
            Você <span className="text-white font-semibold">TRANSFORMA</span> a administração pública.
          </p>
        </div>

        {/* bottom spacer / brand accent */}
        <div className="relative z-10">
          <div className="w-16 h-[3px] bg-blue-400 rounded-full" />
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 md:p-12 bg-white">
        <form
          onSubmit={submit}
          className="w-full max-w-sm"
          data-testid="login-form"
        >
          <div className="flex items-center gap-2 md:hidden mb-6">
            <img src="https://customer-assets-wrfwihn1.emergentagent.net/job_dashboard-ceap/artifacts/b4qjckp1_nova_logo_CEAP_2020-removebg-preview.png" alt="CEAP" className="w-10 h-10 object-contain" style={{ filter: "brightness(0)" }} />
            <div className="font-display font-extrabold text-lg tracking-tight">CRM</div>
          </div>
          <h2 className="font-display text-3xl font-bold text-slate-900">Entrar</h2>
          <p className="text-slate-500 text-sm mt-1">Acesse com seu e-mail corporativo.</p>

          <div className="mt-8 space-y-4">
            <div>
              <Label htmlFor="email">E-mail</Label>
              <Input
                id="email"
                data-testid="login-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="diretoria@ceap.com.br"
                required
                autoFocus
              />
            </div>
            <div>
              <Label htmlFor="password">Senha</Label>
              <Input
                id="password"
                data-testid="login-password"
                type="password"
                value={pw}
                onChange={(e) => setPw(e.target.value)}
                placeholder="••••••"
                required
              />
            </div>
          </div>

          <Button
            data-testid="login-submit"
            type="submit"
            disabled={busy}
            className="w-full mt-6 bg-blue-600 hover:bg-blue-700 h-11 font-semibold"
          >
            <Rocket className="w-4 h-4 mr-2" /> {busy ? "Entrando..." : "Entrar"}
          </Button>
        </form>
      </div>
    </div>
  );
}
